// OBW Journey Matrix — STAFF FORK (ADR-076 §9 copy-fork).
//
// This is the portal-staff fork of the OBW engine. It is a deliberate copy of
// portal-subscriber/src/obw/journeys.ts, scoped to the journeys a Nexa
// consultant launches from the staff dashboard. Per Structure Guide §9 +
// ADR-076, the engine is COPIED per surface and forked — NOT shared at runtime
// (no portal-shared OBW, no iframe of the subscriber portal). The two copies
// drift independently; behavioural fixes are applied to both.
//
// Scope: BOW + card-first SOW (admin-run, account-less; provision at completion
// via POST /portal/{bow,sow}/provision) and POW (partner-apply). POW is NOT
// account-less — a partner IS a subscriber-who-resells, so the staff POW fork is
// admin-assisted for an EXISTING subscriber (ADR-076 Option 2): pa-pick-subscriber
// resolves the picked subscriber + mints an admin POW token; pa-submit provisions
// the brief — keyed to the picked subscriber — via POST /portal/pow/provision.

export type JourneyId = 'bow' | 'sow' | 'partner-apply' | 'b2s';

export type ObwStepId =
  | 'bow-welcome'
  | 'welcome'
  // ADR-124 I3 — "Data residency": the customer's country, which decides the zone it is minted in.
  | 'residency'
  | 'domain-verify'
  | 'subscribe'
  // ADR-078 §4 — B2S (Upgrade) payment stub + conversion bookend.
  | 'payment'
  | 'profile'
  | 'persona'
  | 'bow-complete'
  | 'sow-complete'
  | 'b2s-complete'
  // ADR-076 Option 2 — admin-assisted partner-apply (POW) steps.
  | 'pa-pick-subscriber'
  | 'pa-welcome'
  | 'pa-terms'
  | 'pa-prefill'
  | 'pa-business'
  | 'pa-submit'
  | 'pa-status';

export type AutoFill = 'website-ai' | 'none';
export type ExitPolicy = 'rollback' | 'save';
export type AllowedActor = 'subscriber' | 'nexa-admin';

export interface JourneyConfig {
  journeyId: JourneyId;
  steps: ObwStepId[];
  required: ObwStepId[];
  autoFill: AutoFill;
  exitPolicy: ExitPolicy;
  allowedActors: AllowedActor[];
  loginCheckEffect: 'none';
}

export const JOURNEY_MATRIX: Record<JourneyId, JourneyConfig> = {
  // ADR-073 Amendment 2 (A2.2) / Amendment 3 — BOW. Admin-run, account-less:
  // data is collected through the journey and the User + Account + Domain are
  // provisioned at bow-complete (no card, no subscribe/payment) via the
  // HMAC-validated portal-api route. autoFill='website-ai' for parity with SOW;
  // the analysis only fires once an account exists, so it is inert for the
  // account-less BOW until account-less analysis lands (manual entry works).
  //
  // ⭐ `subscribe` ADDED 29 July 2026 (Jeff's decision, punchlist B13/B14 build) — and it lands
  // in BOTH forks in the same diff. The two journey tables are copies by design (§9 fork), which
  // means a step added to one and not the other produces two BOWs that provision differently:
  // the staff-run one would create beta accounts with no stored plan, and their conversion would
  // silently fall back to asking at upgrade time. Plan step, no payment step (beta = no charge).
  bow: {
    journeyId: 'bow',
    // ADR-124 I3/D5 — `residency` before `domain-verify`: the participant's country picks the
    // zone, and the mount mints the launch token THERE once it is confirmed.
    steps: ['bow-welcome', 'residency', 'domain-verify', 'subscribe', 'profile', 'persona', 'bow-complete'],
    required: ['residency', 'domain-verify', 'subscribe', 'profile', 'persona'],
    autoFill: 'website-ai',
    exitPolicy: 'save',
    allowedActors: ['nexa-admin'],
    loginCheckEffect: 'none',
  },
  // ADR-073 (card-first) — SOW. Admin-run, account-less: collect domain + plan +
  // profile + persona; the (stubbed, ADR-073 Am.1) payment confirms at
  // sow-complete, which provisions the User + Account (paid_pending_activation,
  // selected tier) + Domain via portal-api POST /portal/sow/provision.
  sow: {
    journeyId: 'sow',
    // ADR-124 I3/D5 — `residency` before `domain-verify`: the customer's country picks the zone,
    // and the mount mints the launch token THERE once it is confirmed.
    steps: ['welcome', 'residency', 'domain-verify', 'subscribe', 'profile', 'persona', 'sow-complete'],
    required: ['residency', 'domain-verify', 'subscribe', 'profile', 'persona'],
    autoFill: 'website-ai',
    exitPolicy: 'save',
    allowedActors: ['nexa-admin'],
    loginCheckEffect: 'none',
  },
  // ADR-076 Option 2 — admin-assisted POW. pa-pick-subscriber (the picker) is the
  // FIRST step and mints the admin POW token for the PICKED subscriber; the rest
  // mirror the subscriber partner-apply journey (pa-prefill/pa-submit/pa-status
  // are staff variants that consume the token and provision via /portal/pow/
  // provision instead of the subscriber-JWT /portal/pow/submit).
  'partner-apply': {
    journeyId: 'partner-apply',
    steps: ['pa-pick-subscriber', 'pa-welcome', 'pa-terms', 'pa-prefill', 'pa-business', 'pa-submit', 'pa-status'],
    required: ['pa-pick-subscriber', 'pa-terms', 'pa-prefill', 'pa-business', 'pa-submit'],
    autoFill: 'none',
    exitPolicy: 'save',
    allowedActors: ['nexa-admin'],
    loginCheckEffect: 'none',
  },
  // ADR-078 §4 — B2S (Upgrade): the "tail of SOW" for an EXISTING beta account.
  // Staff-mounted from the Beta-participants Upgrade action; runs IN PLACE on the
  // nexa-admin dashboard (StaffObwModal) — NOT a subscriber-portal tab (C3,
  // ADR-077 mount-owns-exit). Account-less from the engine's view (accountId '');
  // the HMAC B2S token (minted at mount, seeded into `subscribe`) carries the
  // target accountId and is validated server-side at /portal/b2s/convert. Collects
  // only the delta a beta never had: tier → payment(stub) → b2s-complete (convert).
  b2s: {
    journeyId: 'b2s',
    steps: ['subscribe', 'payment', 'b2s-complete'],
    required: ['subscribe', 'payment'],
    autoFill: 'none',
    exitPolicy: 'save',
    allowedActors: ['nexa-admin'],
    loginCheckEffect: 'none',
  },
};

export const MASTER_STEP_LABELS: Record<ObwStepId, string> = {
  'bow-welcome': 'Welcome',
  welcome: 'Welcome',
  residency: 'Data residency',
  'domain-verify': 'Domain Authorization',
  subscribe: 'Plan',
  payment: 'Payment',
  profile: 'Business Profile',
  persona: 'Persona',
  'bow-complete': 'Complete',
  'sow-complete': 'Complete',
  'b2s-complete': 'Complete',
  'pa-pick-subscriber': 'Subscriber',
  'pa-welcome': 'Welcome',
  'pa-terms': 'Terms',
  'pa-prefill': 'Confirm',
  'pa-business': 'Business',
  'pa-submit': 'Submit',
  'pa-status': 'Done',
};
